/** Campaign ordering is progressive enhancement; Shopify remains the source of cards. */
export function priorityHandles(url) {
  if (url.searchParams.has('sort_by') || [...url.searchParams.keys()].some(key => key.startsWith('filter.'))) return [];
  return [...new Set((url.searchParams.get('priority') || '').split(';').map(value => value.trim().toLowerCase()).filter(value => /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value)))].slice(0, 10);
}

const runs = new WeakMap();
function enhance(root) {
  const grid = root.querySelector('ul.product-grid');
  if (!grid || runs.has(grid)) return;
  const url = new URL(location.href);
  const handles = priorityHandles(url);
  if (!handles.length) return;
  // Shopify pagination links do not consistently retain custom query parameters.
  for (const link of root.querySelectorAll('a[href]')) {
    const destination = new URL(link.href, url);
    if (destination.pathname === url.pathname && destination.searchParams.has('page')) {
      destination.searchParams.set('priority', url.searchParams.get('priority'));
      link.href = destination.href;
    }
  }
  if (Number(url.searchParams.get('page') || 1) !== 1) {
    for (const card of grid.querySelectorAll('[data-product-handle]')) {
      if (handles.includes(card.dataset.productHandle)) {
        card.hidden = true;
        card.style.display = 'none';
      }
    }
    runs.set(grid, true);
    return;
  }
  runs.set(grid, true);
  const controller = new AbortController();
  const deadline = setTimeout(() => controller.abort(), 6000);
  root.setAttribute('aria-busy', 'true');
  const found = new Map();
  const cardHandle = card => card.dataset.productHandle;
  const cards = node => [...node.children].filter(card => card.matches('[data-product-handle]'));
  const active = () => !controller.signal.aborted && grid.isConnected && location.pathname === url.pathname && priorityHandles(new URL(location.href)).join(';') === handles.join(';');
  const collect = node => {
    for (const card of cards(node)) {
      const handle = cardHandle(card);
      if (handles.includes(handle) && !found.has(handle)) found.set(handle, card);
    }
  };
  const arrange = () => {
    if (!active()) { controller.abort(); observer.disconnect(); return; }
    observer.disconnect();
    collect(grid);
    for (const card of cards(grid)) {
      if (found.has(cardHandle(card)) && found.get(cardHandle(card)) !== card) {
        // Retain a page sentinel so a final page containing only duplicates terminates scrolling.
        const marker = document.createElement('li');
        marker.dataset.page = card.dataset.page;
        marker.setAttribute('ref', 'cards[]');
        marker.hidden = true;
        marker.style.display = 'none';
        card.replaceWith(marker);
      }
    }
    let anchor = grid.firstChild;
    for (const handle of handles) {
      const card = found.get(handle);
      if (!card) continue;
      // Pinned cards must not affect Horizon's next-page calculation.
      card.dataset.page = '1';
      if (card !== anchor) grid.insertBefore(card, anchor);
      anchor = card.nextSibling;
    }
    observer.observe(grid, { childList: true });
  };
  const observer = new MutationObserver(arrange);
  arrange();
  (async () => {
    // Read the actual collection pages, retaining membership and native card markup.
    // Three concurrent requests, no requests at all without a valid priority parameter.
    const lastPage = Number(grid.dataset.lastPage || 1);
    for (let page = 2; page <= lastPage && found.size < handles.length && active(); page += 3) {
      const pages = await Promise.all(Array.from({ length: Math.min(3, lastPage - page + 1) }, async (_, offset) => {
        const requestURL = new URL(url);
        requestURL.searchParams.set('page', String(page + offset));
        requestURL.searchParams.set('section_id', root.getAttribute('section-id'));
        const response = await fetch(requestURL, { signal: controller.signal });
        if (!response.ok) throw new Error('Collection page unavailable');
        return new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('ul.product-grid');
      }));
      if (!active()) return;
      for (const otherGrid of pages) if (otherGrid) collect(otherGrid);
    }
    arrange();
  })().catch(() => { /* Fail open with the successfully resolved products. */ }).finally(async () => {
    clearTimeout(deadline);
    // Reveal the final order once, with the first images ready where possible.
    if (!controller.signal.aborted) arrange();
    const images = [...grid.querySelectorAll('img')].slice(0, 4);
    for (const image of images) image.loading = 'eager';
    await Promise.race([
      Promise.allSettled(images.map(image => image.decode?.())),
      new Promise(resolve => setTimeout(resolve, 700)),
    ]);
    root.removeAttribute('aria-busy');
    document.documentElement.classList.remove('nv-priority-loading');
  });
}

if (typeof document !== 'undefined') {
  const scan = () => document.querySelectorAll('results-list[data-collection-priority]').forEach(enhance);
  new MutationObserver(scan).observe(document.documentElement, { childList: true, subtree: true });
  window.addEventListener('popstate', scan);
  scan();
}
